import React, { useEffect, useRef, useState } from "react";
import { getAllNewslettersData } from "../services/Newsletters";
import { useSelector } from "react-redux";
import TableStructure from "../CommonComponents/TableStructure";
import { Box } from "@chakra-ui/react";

const SubscribedUsersTable = () => {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting state
  const session = useSelector((state) => state.session);
  const limit = 50; // Number of records per page
  
  // Fetch newsletters from the API
  const fetchNewsletters = async (page, term) => {
    setLoading(true);
    try {
      const result = await getAllNewslettersData(limit, page, term);

      if (result?.data) {
        setRecords(formatData(result?.data?.data || []));
        setTotalPages(Math.ceil(result?.data?.total / limit));
        setTotalRecords(result?.data?.total);

        if (result?.data?.page_number && parseInt(result?.data?.page_number) !== currentPage) {
          setCurrentPage(parseInt(result?.data?.page_number));
        }
      }
    } catch (error) {
      console.error("Error fetching newsletters:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatData = (data) => {
    return data?.map((item, index) => {
      return {
        name: item?.name || "-",
        email: item?.email || "-",
        phone: item?.phone || "-",
        subscribedDate: item?.createdAt ? new Date(item.createdAt).toLocaleString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }) : "-",
        whatsappConsent: item?.whatsappConsent,
        status: item?.isSubscribed ? "Subscribed" : "Unsubscribed"
      }
    })
  }

  const columns = [
    
    {
      label: "Name",
      accessor: "name",
      width: "15%"
    },
    {
      label: "Email",
      accessor: "email",
      width: "20%"
    },
    {
      label: "Phone Number",
      accessor: "phone",
      width: "12%"
    },
    {
      label: "Subscribed Date",
      accessor: "subscribedDate",
      width: "18%"
    },
    {
      label: "WhatsApp Consent",
      accessor: "whatsappConsent",
      width: "15%",
      render: (value) => (
        <Box
          display="inline-flex"
          alignItems="center"
          px={3}
          py={1.5}
          borderRadius="full"
          fontSize="sm"
          fontWeight="600"
          bg={value ? "blue.100" : "gray.100"}
          color={value ? "blue.800" : "gray.800"}
          border="1px solid"
          borderColor={value ? "blue.200" : "gray.200"}
        >
          {value ? "Opted-In" : "Opted-Out"}
        </Box>
      )
    },
    {
      label: "Subscription",
      accessor: "status",
      width: "20%",
      render: (value) => (
        <Box
          display="inline-flex"
          alignItems="center"
          px={3}
          py={1.5}
          borderRadius="full"
          fontSize="sm"
          fontWeight="600"
          bg={value === "Subscribed" ? "green.100" : "red.100"}
          color={value === "Subscribed" ? "green.800" : "red.800"}
          border="1px solid"
          borderColor={value === "Subscribed" ? "green.200" : "red.200"}
        >
          {value}
        </Box>
      )
    }
  ]

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false; // Skip the first render
      setCurrentPage(1); // Reset to page 1 for a new search
      setSearchText(searchTerm);
      fetchNewsletters(1, searchTerm);
    }
  };

  const sortRecords = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc"; // Update sortOrder first
    setSortOrder(newSortOrder);
  
    const sorted = [...records].sort((a, b) =>
      newSortOrder === "asc"
        ? a.email.localeCompare(b.email)
        : b.email.localeCompare(a.email)
    );
  
    setRecords(sorted);
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchNewsletters(currentPage, searchText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleClear = () => {
    setSearchText('');
    fetchNewsletters(1, "");
  };

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };
  return (
      <TableStructure
        isAuthenticated={session?.isAuthenticated}
        handleSearch={handleSearch}
        handleClear={handleClear}
        handlePageChange={handlePageChange}
        totalRecords={totalRecords}
        currentPage={currentPage}
        totalPages={totalPages}
        sortRecords={sortRecords}
        sortOrder={sortOrder}
        loading={loading}
        records={records}
        type="subscribed-users"
        title="Subscribed user"
        columns={columns}
        disableRowClick={true}
         sx={{
    '& td': {
      py: 8
    },
    '& tbody tr': {
       cursor: 'default !important',
      '&:hover': {
        cursor: 'default !important'
      }}
  }}
    />
  );
};

export default SubscribedUsersTable;
